import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { categories } from '../context/ProductContext';
import { FaShoppingCart } from 'react-icons/fa';
import { HiMenu, HiX } from 'react-icons/hi';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const { getTotalItems } = useCart();
  const location = useLocation();

  const toggleMenu = (): void => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleScroll = (): void => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const handleLinkClick = (): void => {
    setIsMenuOpen(false);
  };

  const isActive = (path: string): boolean => location.pathname === path;
  const cartCount = getTotalItems();

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-medium border-b border-gray-100' 
          : 'bg-white shadow-sm'
      }`}>
        <div className="container">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <img 
                  src="/logo/fusionmartlogo.png" 
                  alt="Fusion Mart CHT" 
                  className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-110" 
                />
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-display font-bold text-secondary block">FUSION MART</span>
                <span className="text-xs text-accent font-medium">CHT</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              <Link 
                to="/shop" 
                className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isActive('/shop') && !location.search
                    ? 'bg-primary/10 text-primary' 
                    : 'text-secondary hover:bg-neutral hover:text-primary'
                }`}
              >
                All Collections
              </Link>
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.id}`}
                  className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                    location.search.includes(`category=${category.id}`)
                      ? 'bg-primary/10 text-primary' 
                      : 'text-secondary hover:bg-neutral hover:text-primary'
                  }`}
                >
                  {category.name}
                </Link>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-4">
              {/* Cart Icon */}
              <Link 
                to="/cart" 
                className="relative p-3 rounded-lg hover:bg-neutral transition-colors duration-200 group min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <FaShoppingCart className="w-6 h-6 text-secondary group-hover:text-primary transition-colors" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center shadow-medium animate-pulse">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Link>

              {/* Mobile Menu Button */}
              <button 
                className="lg:hidden p-3 rounded-lg hover:bg-neutral transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                onClick={toggleMenu}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <HiX className="w-6 h-6 text-secondary" />
                ) : (
                  <HiMenu className="w-6 h-6 text-secondary" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Slide from Right - Outside Nav Container */}
      <div className={`fixed inset-0 z-[1001] lg:hidden transition-opacity duration-300 ${
        isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}>
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={toggleMenu}
        />
        {/* Menu Panel - Slides from Right */}
        <div className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transition-transform duration-300 ease-out ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="h-full flex flex-col">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-display font-bold text-secondary">Menu</h2>
              <button 
                onClick={toggleMenu}
                className="p-2 rounded-lg hover:bg-neutral transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close menu"
              >
                <HiX className="w-6 h-6 text-secondary" />
              </button>
            </div>
            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto py-4">
              <div className="space-y-1 px-4">
<Link 
                to="/shop" 
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  isActive('/shop') && !location.search
                    ? 'bg-primary/10 text-primary' 
                    : 'text-secondary hover:bg-neutral'
                }`}
                onClick={handleLinkClick}
              >
                All Collections
              </Link>
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/shop?category=${category.id}`}
                    className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                      location.search.includes(`category=${category.id}`)
                        ? 'bg-primary/10 text-primary' 
                        : 'text-secondary hover:bg-neutral'
                    }`}
                    onClick={handleLinkClick}
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
